import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Stack, Text } from '@wapharma/design-system';
import { validatePasswordResetToken } from '../auth/passwordReset';
import {
  getStoredPasswordResetEmail,
  passwordResetToastMessage,
  submitPasswordResetEmail,
} from '../auth/password-reset-email';
import { AdminAuthLayout } from '../components/auth/AdminAuthLayout';
import authStyles from '../components/auth/auth-layout.module.css';
import { authFooterLine, resolveDeployOrgBrand } from '../lib/deployOrgBrand';
import { useToast } from '../contexts/ToastContext';
import { getDisplayError } from '../apiErrors';

type Step = 'email' | 'code';

function validateEmailField(value: string): string | undefined {
  const v = value.trim();
  if (!v) return "L'e-mail est requis.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Adresse e-mail invalide.';
  return undefined;
}

export function ForgotPasswordPage() {
  const { showToast, clearToasts } = useToast();
  const navigate = useNavigate();
  const deployBrand = resolveDeployOrgBrand();

  const [email, setEmail] = useState(() => getStoredPasswordResetEmail());
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const resetInFlightRef = useRef(false);

  const emailError = useMemo(() => {
    if (!emailTouched && !submitAttempted) return undefined;
    return validateEmailField(email);
  }, [email, emailTouched, submitAttempted]);

  useEffect(() => () => clearToasts(), [clearToasts]);

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (resetInFlightRef.current || pending) return;
    setSubmitAttempted(true);
    const err = validateEmailField(email);
    if (err) return;

    resetInFlightRef.current = true;
    setPending(true);
    try {
      const result = await submitPasswordResetEmail(email);
      showToast(passwordResetToastMessage(result), {
        tone: result === 'throttled' ? 'error' : 'success',
      });
      setCode('');
      setStep('code');
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      resetInFlightRef.current = false;
      setPending(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    const token = code.trim();
    if (!/^\d{6}$/.test(token)) {
      showToast('Code invalide. Saisissez les 6 chiffres.', { tone: 'error' });
      return;
    }

    setPending(true);
    try {
      await validatePasswordResetToken(token);
      navigate(`/reset-password?token=${encodeURIComponent(token)}`);
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      setPending(false);
    }
  };

  const resendCode = async () => {
    if (resetInFlightRef.current || pending) return;
    const err = validateEmailField(email);
    if (err) {
      showToast(err, { tone: 'error' });
      return;
    }

    resetInFlightRef.current = true;
    setPending(true);
    try {
      const result = await submitPasswordResetEmail(email);
      showToast(passwordResetToastMessage(result), {
        tone: result === 'throttled' ? 'error' : 'success',
      });
      setCode('');
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      resetInFlightRef.current = false;
      setPending(false);
    }
  };

  return (
    <AdminAuthLayout
      brandTitle="Réinitialisation"
      brandBody="Recevez un code par e-mail pour définir un nouveau mot de passe personnel."
    >
      <Stack gap="xl" className={authStyles.formStack}>
        <Card elevation="flat" padding="lg" className={authStyles.authFormCard}>
          <Stack gap="lg">
            <Stack gap="sm">
              <div className={authStyles.formHeading}>
                <p className={authStyles.formTitle}>Mot de passe oublié</p>
              </div>
              <hr className={authStyles.formHeadingDivider} />
            </Stack>

            {step === 'email' ? (
              <form onSubmit={(e) => void submitEmail(e)} noValidate>
                <Stack gap="md">
                  <Input
                    label="E-mail"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    error={emailError}
                    autoFocus
                    onChange={(ev) => setEmail(ev.target.value)}
                    onBlur={() => setEmailTouched(true)}
                  />
                  <Button type="submit" disabled={pending} className={authStyles.submitBtn}>
                    {pending ? 'Envoi…' : 'Envoyer le code'}
                  </Button>
                  <div className={authStyles.authFormFooter}>
                    <Link to="/login" className={authStyles.authNavLink}>
                      Retour à la connexion
                    </Link>
                  </div>
                </Stack>
              </form>
            ) : (
              <form onSubmit={(e) => void submitCode(e)} noValidate>
                <Stack gap="md">
                  <Input
                    label="Code reçu par e-mail"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    value={code}
                    autoFocus
                    onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <Button type="submit" disabled={pending} className={authStyles.submitBtn}>
                    {pending ? 'Vérification…' : 'Continuer'}
                  </Button>
                  <div className={authStyles.authSecondaryActions}>
                    <button
                      type="button"
                      className={authStyles.authActionLink}
                      disabled={pending || !email.trim()}
                      onClick={() => void resendCode()}
                    >
                      Renvoyer le code
                    </button>
                    <span className={authStyles.authLinkSeparator} aria-hidden>
                      ·
                    </span>
                    <button
                      type="button"
                      className={authStyles.authActionLink}
                      disabled={pending}
                      onClick={() => {
                        setStep('email');
                        setCode('');
                        setSubmitAttempted(false);
                      }}
                    >
                      Utiliser un autre e-mail
                    </button>
                  </div>
                  <div className={authStyles.authFormFooter}>
                    <Link to="/login" className={authStyles.authNavLink}>
                      Retour à la connexion
                    </Link>
                  </div>
                </Stack>
              </form>
            )}
          </Stack>
        </Card>

        <Text size="sm" c="secondary" ta="center" ff="body" className={authStyles.mobileFooter}>
          {authFooterLine(deployBrand)}
        </Text>
      </Stack>
    </AdminAuthLayout>
  );
}
